import { Notification } from "./Notification";
import { v4 as uuidv4 } from "uuid";
import { action, computed, makeObservable, observable } from "mobx";

export function Input(pConfig, pData) {
  Notification.call(this);

  this.mConfig = pConfig;
  this.mName = '';
  this.mDeviceType = '';
  this.mDevice = null;
  this.mPin = null;
  this.mUUID = '';

  this.fromJson(pData);

  makeObservable(this, {
    mName: observable,
    mUUID: observable,
    name: computed,
    uuid: computed,
    deviceType: computed,
    setName: action,
    fromJson: action,
  });
}

Input.prototype = Object.create(Notification.prototype);
Input.prototype.constructor = Input;

Input.prototype.setDirty = function() {
  // WARNING : This function is overridden in the assembler (vention_assembler/client/src/machinelogic.js)
  this.mConfig.setDirty();
};

Input.prototype.getConfig = function() {
  return this.mConfig;
};

Input.prototype.getConfigManager = function() {
  return this.mConfig.getConfigManager();
};

Input.prototype.getUUID = function() { return this.mUUID; };

Input.prototype.getName = function() { return this.mName; };

Input.prototype.setName = function(pName) {
  if (this.mName === pName) {
    return;
  }

  const lThis = this;
  const _lNewName = this.mConfig.uniqueInputName(pName);
  this.mConfig.mLibrary.getAllApplications().forEach(pApp => {
    const lDigitalInputDisplayWidgets = pApp.mUI.listAllWidgets()
      .filter(pWidget => pWidget.getType() == 'digitalInputDisplay')
      .filter(pWidget => pWidget.getProps().input == lThis.getName());
    if (lDigitalInputDisplayWidgets.length != 0) {
      lDigitalInputDisplayWidgets.forEach(pWidget => {
        pWidget.getProps().input = _lNewName;
      });
      pApp.setDirty();
    }
  });
  this.mName = _lNewName;
  this.setDirty();
  this.notify('onNameChanged', this.mName);
  this.mConfig.notify('onInputNameChanged', this.mName);
};

Input.prototype.getDeviceType = function() { return this.mDeviceType; };

Input.prototype.setDeviceType = function(pDeviceType) {
  // WARNING : This function is overridden in the assembler (vention_assembler/client/src/machinelogic_input3d.js)
  let lSuccess = true;

  // if this input is NOT the only one configured on the current device port (checking occupancy in mInputMap and mOutputMap)
  // or the current pin is greater than the supported number of pins for the selected deviceType,
  // we will have search for a valid combination.
  if (Object.values(this.mConfig.mInputMap[this.getDevice()]).filter((val) => val == true).length > 1
    || Object.values(this.mConfig.mOutputMap[this.getDevice()]).filter((val) => val == true).length > 0
    || this.getPin() >= this.mConfig.getNumberOfPinsOnDeviceType(pDeviceType)) {
    // Free up space at the device-pin combination so our search function considers it as an option too
    if (this.isValid()) {
      this.mConfig.mInputMap[this.getDevice()][this.getPin()] = false;
      if (this.mConfig._isDeviceEmpty(this.getDevice())) {this.mConfig.mDeviceTypeMap[this.getDevice()] = '?';}
    }
    const lNextParams = this.mConfig._getNextInputParametersByDeviceType(pDeviceType);
    if (lNextParams) {
      this.mDeviceType = pDeviceType;
      this.setDevice(lNextParams.device);
      this.setPin(lNextParams.pin);
    } else {
      // no valid combination found, so you would want to revert the map to the original state and return as a failed attempt
      this.mConfig.updateActuatorIOMap();
      lSuccess = false;
      return lSuccess;
    }
  }
  else {
    this.mDeviceType = pDeviceType;
  }

  this.mConfig.updateActuatorIOMap();
  this.setDirty();
  this.notify('onTypeChanged', this.mDeviceType);
  return lSuccess;
};

/**
 * Returns the Controller that this input belongs to.
 * @returns {String} Unique ID of the controller.
 */
Input.prototype.getControllerId = function() { return this.mConfig.getController().getUUID(); };

Input.prototype.isValid = function() {
  return (undefined != this.mDevice && null != this.mDevice && undefined != this.mPin && null != this.mPin && undefined != this.mDeviceType);
};

Input.prototype.getDeviceRange = function() {
  return this.mConfig.mDeviceInfo.devices;
};

Input.prototype.getDevice = function() { return this.mDevice; };

/**
 * Set input pin device, or
 * in case the pin is already in use
 * set another available pin
 *
 * @param {!number} pDevice - control module number [1,2,3]
 * @returns {void}
 */
Input.prototype.setDevice = function(pDevice) {
  // WARNING : This function is overridden in the assembler (vention_assembler/client/src/machinelogic_input3d.js)
  let lNextParams;

  // Find next device and input pins available for a specific device id
  function lNextInputPinByDevice (pInput) {
    const lMaxPins = pInput.mConfig.getNumberOfPinsOnDeviceType(pInput.getDeviceType());

    for(const pin in pInput.mConfig.mInputMap[pDevice]) {
      if(!pInput.mConfig.mInputMap[pDevice][pin] && pin < lMaxPins) {
        return { 'device': parseInt(pDevice), 'pin': parseInt(pin) };
      }
    }
  }

  if (this.mConfig._isPinUsed(pDevice, this.getPin(), 'input')) {
    lNextParams = lNextInputPinByDevice(this);
    pDevice = lNextParams.device;
    this.setPin(lNextParams.pin);
  }
  this.mDevice = pDevice;
  this.mConfig.updateActuatorIOMap();
  this.setDirty();
  this.notify('onDeviceChanged', this.mDevice);
};

Input.prototype.getPinRange = function() {
  return this.mConfig.mDeviceInfo.pins;
};

Input.prototype.getPin = function() { return this.mPin; };

Input.prototype.setPin = function(pPin) {
  this.mPin = pPin;
  this.mConfig.updateActuatorIOMap();
  this.setDirty();
  this.notify('onPinChanged', this.mPin);
};

Input.prototype.toJson = function() {
  return {
    'category': 'config',
    'name': 'alias',
    'parameters': {
      'aliasName': this.mName,
      'uuid': this.mUUID,
      'controllerId': this.mConfig.getController().getUUID(),
      'category': 'input',
      'deviceType': this.mDeviceType,
      'parameters': {
        'device': this.mDevice,
        'pin': this.mPin
      }
    }
  };
};

Input.prototype.fromJson = function(pInstruction) {
  if(pInstruction && pInstruction.parameters) {
    if (pInstruction.parameters.name == 'digitalInput') { pInstruction.parameters.deviceType = 'digitalIOModule'; } // for backward compatibility.
    this.mName = pInstruction.parameters.aliasName || this.mName;
    this.mUUID = pInstruction.parameters.uuid || uuidv4();
    this.mDevice = pInstruction.parameters.parameters.device || this.mDevice;
    this.mPin = pInstruction.parameters.parameters.pin == 0 ? 0 : (pInstruction.parameters.parameters.pin || this.mPin); // 0 || null => null
    this.mDeviceType = pInstruction.parameters.deviceType || this.mDeviceType;
  }
};

Object.defineProperty(
  Input.prototype,
  'name',
  {
    get: function () {
      return this.getName();
    }
  }
);

Object.defineProperty(
  Input.prototype,
  'deviceType',
  {
    get: function () {
      return this.getDeviceType();
    }
  }
);

Object.defineProperty(
  Input.prototype,
  'uuid',
  {
    get: function () {
      return this.getUUID();
    }
  }
);
